import { Component, OnDestroy, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AppUpdateComponent,
  CrowdctrlToastService,
  PageComponent,
  RemoteConfigReloadComponent,
  ThemeService,
  ThemePreviewItemComponent,
  CrowdctrlListComponent,
} from '@crowdctrl/shared';
import { Subscription } from 'rxjs';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonToggle,
  IonButton,
  AlertController,
} from '@ionic/angular/standalone';
import { RouterLink } from '@angular/router';
import { VERSION } from '../../../version';
import { BreadcrumbPreferenceService } from '../../services/breadcrumb-preference.service';
import { WorkspaceInfoComponent } from '../workspaces/workspace-tabs/workspace-info/workspace-info.component';
import { SessionInfoComponent } from '../workspaces/workspace-tabs/sessions/session-tabs/session-info/session-info.component';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    WorkspaceInfoComponent,
    SessionInfoComponent,
    PageComponent,
    RemoteConfigReloadComponent,
    AppUpdateComponent,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonCardContent,
    IonItem,
    IonLabel,
    CrowdctrlListComponent,
    IonToggle,
    IonButton,
    ThemePreviewItemComponent,
    RouterLink,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css',
})
export class SettingsComponent implements OnInit, OnDestroy {
  pageTitle = 'Settings';
  version = VERSION;
  currentThemeName: string | null = null;
  /** Only show PWA update card on web; hide on native app where it does not apply (avoids blank space). */
  showPwaUpdateCard: boolean | null = null;

  private breadcrumbPreferenceService = inject(BreadcrumbPreferenceService);
  private themeService = inject(ThemeService);
  private cdr = inject(ChangeDetectorRef);
  private alertController = inject(AlertController);
  private toastService = inject(CrowdctrlToastService);
  private themeSubscription?: Subscription;
  showBreadcrumbs = this.breadcrumbPreferenceService.getShowBreadcrumbs();

  async ngOnInit(): Promise<void> {
    this.currentThemeName = this.themeService.getCurrentTheme();
    this.themeSubscription = this.themeService.currentTheme$.subscribe((themeName) => {
      this.currentThemeName = themeName;
      this.cdr.markForCheck();
    });

    try {
      const { Capacitor } = await import('@capacitor/core');
      this.showPwaUpdateCard = !Capacitor.isNativePlatform();
    } catch {
      this.showPwaUpdateCard = true;
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
  }

  onBreadcrumbsToggleChange(event: CustomEvent): void {
    const checked = (event.detail as { checked: boolean }).checked;
    this.showBreadcrumbs = checked;
    this.breadcrumbPreferenceService.setShowBreadcrumbs(checked);
  }

  async onClearLocalStorageClick(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Clear Local Storage',
      message: 'This will remove all locally stored data. You may need to sign in again. Continue?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Clear', role: 'destructive' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    if (role === 'destructive') {
      localStorage.clear();
      await this.toastService.show({
        message: 'Local storage cleared.',
        duration: 2000,
        color: 'success',
      });
    }
  }
}
